import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FeerdMark } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1771927579674-e20425bfb859?auto=format&fit=crop&w=1400&q=70";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    <input data-testid={testId} {...props} className="mt-3 w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#D8BFD8] outline-none py-3 text-ivory placeholder:text-[#4a4a4a] transition-colors duration-300" />
  </label>
);

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "signin") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
    } catch (err) { setError(errMsg(err)); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A]" data-testid="auth-page">
      <div className="relative hidden md:block overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-60" style={{ backgroundImage: `url(${BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <p className="absolute bottom-12 left-12 font-display italic text-3xl text-ivory/80 max-w-sm leading-snug">“A quiet mind, built to keep your confidence.”</p>
      </div>
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col justify-center px-8 md:px-20 py-16">
        <button onClick={() => navigate("/")} className="flex items-center gap-3 mb-16" data-testid="auth-home-link">
          <FeerdMark /><span className="font-display text-2xl tracking-[0.18em] text-ivory">FEERD</span>
        </button>
        <h1 className="font-display text-4xl sm:text-5xl text-ivory tracking-tight">{mode === "signin" ? "Return to the room." : "Enter for the first time."}</h1>
        <p className="mt-4 text-sm text-[#8A8A8A]">{mode === "signin" ? "Sign in to continue your conversations." : "Create a private account. Your data stays yours."}</p>
        <form onSubmit={submit} className="mt-12 space-y-8 max-w-md" data-testid="auth-form">
          {mode === "register" && <Field label="Name" testId="auth-name-input" value={form.name} onChange={set("name")} placeholder="How Feerd should address you" />}
          <Field label="Email" testId="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@domain.com" autoComplete="email" />
          <Field label="Password" testId="auth-password-input" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} placeholder={mode === "register" ? "At least 8 characters" : "••••••••"} autoComplete={mode === "signin" ? "current-password" : "new-password"} />
          {error && <p className="text-sm text-[#e2a6a6]" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full py-4 rounded-full bg-[#C0C0C0] text-[#0A0A0A] text-xs tracking-[0.24em] uppercase hover:bg-[#F8F4E6] transition-colors duration-300 disabled:opacity-50">
            {busy ? "One moment…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button onClick={() => { setMode(mode === "signin" ? "register" : "signin"); setError(""); }} data-testid="auth-toggle-mode" className="mt-8 text-left text-xs tracking-[0.18em] uppercase text-[#8A8A8A] hover:text-[#D8BFD8] transition-colors max-w-md">
          {mode === "signin" ? "No account yet? Create one" : "Already have an account? Sign in"}
        </button>
      </motion.div>
    </div>
  );
}
